import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { formatCents, parseDollarsToCents } from '@crave/shared';
import { Button, Card, Field, FormGrid, Spicy, TextAreaField } from '@crave/ui';
import { ApiError } from '@crave/api-client';
import { api } from '../api.ts';
import { ConfirmDelete } from '../components/ConfirmDelete.tsx';
import { HistoryLink } from '../components/HistoryLink.tsx';
import { Notice } from '../components/ui.tsx';
import { useStaff } from '../lib/staff.tsx';

interface Location {
  id: string;
  slug: string;
  name: string;
  orderingEnabled: boolean;
  orderingPaused: boolean;
  pausedMessage: string | null;
  taxRateBps: number | null;
  minLeadMinutes: number;
  defaultSlotCapacity: number;
  slotMinutes: number;
  noShowGraceMinutes: number;
  pickupInstructions: string | null;
  /** Nutrition Facts on the ordering site (off until the owner turns them on). */
  showNutrition?: boolean;
  allergenNotice?: string;
  /** Menu prices on the ordering site (on unless the owner hides them; migration 0025). */
  showPrices?: boolean;
  schedule: {
    weekday: number;
    opensAt: string;
    closesAt: string;
    isEnabled: boolean;
    slotCapacity: number | null;
  }[];
  blockers: { code: string; message: string; item?: string; modifier?: string }[];
  /** Not blockers: orders still work, but something is off, like customer emails. */
  warnings?: { code: string; message: string }[];
}
interface MenuItemRow {
  id: string;
  slug: string;
  name: string;
  status: string;
  servings_per_item?: number;
  /** Shows SPICY beside the name wherever the pizza is offered (brand manual, #65). */
  is_spicy?: boolean;
  prices: { id: string; price_cents: number; status: string; location_id: string | null }[] | null;
  modifiers:
    | { id: string; code: string; name: string; kind: string; price_delta_cents: number | null }[]
    | null;
  costing:
    | { food_cost_cents: number | null; has_complete_cost: boolean; food_cost_pct: number | null }[]
    | null;
  uses?: Record<string, number>;
  can_delete?: boolean;
  delete_blocked_reason?: string | null;
}
interface ModifierRow {
  id: string;
  code: string;
  name: string;
  kind: string;
  price_delta_cents: number | null;
  is_active: boolean;
  menu_items: number;
  order_lines: number;
  can_delete: boolean;
  delete_blocked_reason: string | null;
}
interface RecipeRow {
  id: string;
  name: string;
  kind: string;
  menu_item_id: string | null;
  versions: {
    id: string;
    status: string;
    nutrition?: { complete: boolean; missing?: string[] | null } | null;
  }[];
}

/** The parts of a mutation a save status needs. */
interface SaveState {
  isSuccess: boolean;
  isError: boolean;
  error: unknown;
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const hhmm = (t: string) => t.slice(0, 5);
const HHMM = /^\d{2}:\d{2}$/;
const errorText = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong.');

/** The notice every location starts with (locations.allergen_notice default). */
const STANDARD_ALLERGEN_NOTICE =
  'Prepared in a kitchen that handles wheat, milk and pork; we cannot guarantee against cross-contact.';

/** Uses that retiring does not solve: the birthday pizza has to be changed in Loyalty first. */
const BIRTHDAY_USES = new Set(['birthday_rules', 'birthday_rewards']);

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const listText = (xs: string[]) =>
  xs.length <= 1 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
/** Deletes take the typed reason as ?reason= (the API client sends no DELETE body). */
const withReason = (path: string, reason: string | undefined) =>
  reason ? `${path}?reason=${encodeURIComponent(reason)}` : path;

/**
 * Page layout. Service days are a Day / Opens / Closes grid that stacks per day on a phone; price
 * rows share label, control and hint lines (subgrid) so the button always sits beside the input.
 * Under each row a tools line holds slices, retire, delete and history.
 */
const READINESS_CSS = `
.rd-page { display: grid; grid-template-columns: minmax(0, 1fr); gap: 20px; }
.rd-actions { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.rd-check { display: flex; align-items: center; gap: 10px; min-height: var(--touch); line-height: 1.3; }
.rd-check input { flex: none; width: 20px; height: 20px; margin: 0; accent-color: var(--crave-red); }
.rd-muted { font-size: 14px; line-height: 1.4; color: var(--ink-soft); }

.rd-days {
  display: grid;
  grid-template-columns: minmax(130px, 180px) minmax(0, 240px) minmax(0, 240px);
  column-gap: 16px;
  row-gap: 8px;
  align-items: center;
}
.rd-days-head, .rd-day { display: contents; }
.rd-days-head > span { font-weight: 600; line-height: 1.3; }
.rd-time { display: grid; gap: 6px; min-width: 0; }
.rd-time-label { display: none; font-size: 14px; font-weight: 600; line-height: 1.3; color: var(--ink-soft); }
.rd-time input, .rd-small-input {
  width: 100%;
  min-width: 0;
  height: var(--touch);
  padding: 0 12px;
  border: 2px solid var(--line);
  border-radius: var(--radius-sm);
  background: var(--paper);
  color: var(--ink);
}

.rd-prices { display: grid; }
.rd-price {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 140px auto;
  column-gap: 12px;
  row-gap: 6px;
  align-items: start;
  padding: 12px 0;
}
.rd-price + .rd-price { border-top: 1px solid var(--line); }
.rd-price-name { grid-column: 1; grid-row: 1; align-self: end; line-height: 1.3; min-width: 0; }
.rd-price-meta { grid-column: 1; grid-row: 2; align-self: start; font-size: 13px; line-height: 1.35; color: var(--ink-soft); min-width: 0; }
.rd-price > .crave-field { grid-column: 2; grid-row: 1 / span 3; grid-template-rows: subgrid; }
.rd-price > .crave-field > .crave-field-label { align-self: end; }
.rd-price-btn { grid-column: 3; grid-row: 2; align-self: center; }
.rd-price-status { grid-column: 1 / -1; grid-row: 4; }
.rd-price-ro { grid-template-columns: minmax(0, 1fr) auto; }
.rd-price-ro .rd-price-name, .rd-price-ro .rd-price-meta { align-self: start; }
.rd-price-owner { grid-column: 2; grid-row: 1 / span 2; align-self: center; font-size: 13px; color: var(--ink-soft); }
.rd-badge { display: inline-block; margin-left: 8px; padding: 2px 8px; border-radius: 999px; background: var(--mist); color: var(--ink-soft); font-size: 12px; font-weight: 700; vertical-align: 2px; white-space: nowrap; }

.rd-tools { grid-column: 1 / -1; grid-row: 5; display: flex; flex-wrap: wrap; align-items: center; gap: 8px 24px; padding-top: 4px; min-width: 0; }
.rd-price-ro > .rd-tools { grid-row: 3; }
.rd-slices { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.rd-spicy { display: flex; align-items: center; gap: 8px; min-height: 44px; cursor: pointer; }
.rd-spicy input { width: 18px; height: 18px; margin: 0; flex: none; }
.rd-slices label { font-size: 14px; font-weight: 600; line-height: 1.3; }
.rd-small-input { width: 72px; padding: 0 10px; }
.rd-small-input[aria-invalid="true"] { border-color: var(--crave-red); }
.rd-item-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 12px; margin-left: auto; }
.rd-tools-msg { flex-basis: 100%; font-size: 14px; line-height: 1.4; }
.rd-gaps { border: 2px solid var(--warn); border-radius: var(--radius-sm); padding: 10px 12px; font-size: 14px; line-height: 1.45; }
.rd-gaps ul { margin: 6px 0; padding-left: 20px; }
@supports not (grid-template-rows: subgrid) {
  .rd-price-btn { grid-row: 1 / span 3; align-self: end; }
}

@media (max-width: 560px) {
  .rd-days { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); row-gap: 0; }
  .rd-days-head { display: none; }
  .rd-day > .rd-check { grid-column: 1 / -1; }
  .rd-day + .rd-day > .rd-check { border-top: 1px solid var(--line); }
  .rd-time { padding-bottom: 12px; }
  .rd-time-label { display: block; }

  .rd-price { grid-template-columns: minmax(0, 1fr) auto; }
  .rd-price-name { grid-column: 1 / -1; grid-row: 1; }
  .rd-price-meta { grid-column: 1 / -1; grid-row: 2; margin-bottom: 6px; }
  .rd-price > .crave-field { grid-column: 1; grid-row: 3 / span 3; }
  .rd-price-btn { grid-column: 2; grid-row: 4; }
  .rd-price-status { grid-row: 6; }
  .rd-price-owner { grid-column: 1 / -1; grid-row: 3; }
  .rd-tools { grid-row: 7; column-gap: 12px; }
  .rd-price-ro > .rd-tools { grid-row: 4; }
  .rd-item-actions { margin-left: 0; }
  @supports not (grid-template-rows: subgrid) {
    .rd-price-btn { grid-row: 3 / span 3; }
  }
}
`;

/** "7", "7.0", "7%", "7,5" and " 7.25 % " all parse; anything else is null. */
function parsePercent(input: string): number | null {
  const t = input.replace(/\s+/g, '').replace(/%$/, '').replace(',', '.');
  if (!/^(\d+(\.\d{1,2})?|\.\d{1,2})$/.test(t)) return null;
  return Number(t);
}
const parseWhole = (input: string): number | null => {
  const t = input.trim();
  return /^\d+$/.test(t) ? Number(t) : null;
};

/** Why a pizza on the menu would show no Nutrition Facts, or null when it is complete. */
function nutritionGap(item: MenuItemRow, recipes: RecipeRow[]): string | null {
  const recipe = recipes.find((r) => r.menu_item_id === item.id);
  if (!recipe) return 'has no recipe yet';
  const live = recipe.versions.find((v) => v.status === 'published');
  if (!live) return 'has no published recipe yet';
  if (live.nutrition?.complete) return null;
  const missing = live.nutrition?.missing ?? [];
  return missing.length > 0
    ? `is missing nutrition for ${listText(missing)}`
    : 'is missing nutrition for some ingredients';
}

export function Readiness() {
  const qc = useQueryClient();
  const staff = useStaff();
  const canToggle = staff.can('ordering.toggle');
  const canPrice = staff.can('prices.confirm');
  const canAddOns = staff.can('modifiers.manage');
  const canIntegrations = staff.can('settings.manage');
  const canMenu = staff.can('menu.manage');
  const canDelete = staff.can('records.delete');
  const canRecipes = staff.can('recipes.manage');

  const locs = useQuery({
    queryKey: ['admin-locations'],
    queryFn: async () => (await api.get<{ items: Location[] }>('/admin/locations')).data.items,
  });
  const items = useQuery({
    queryKey: ['admin-menu'],
    queryFn: async () => (await api.get<{ items: MenuItemRow[] }>('/admin/menu/items')).data.items,
  });
  const modifiers = useQuery({
    queryKey: ['admin-modifiers'],
    enabled: canAddOns,
    queryFn: async () => (await api.get<{ items: ModifierRow[] }>('/admin/modifiers')).data.items,
  });
  // Recipes tell which pizzas have complete nutrition; only people who manage recipes can read them.
  const recipes = useQuery({
    queryKey: ['readiness-recipes'],
    enabled: canRecipes,
    queryFn: async () => (await api.get<{ items: RecipeRow[] }>('/admin/recipes')).data.items,
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['admin-locations'] });
    void qc.invalidateQueries({ queryKey: ['admin-menu'] });
    void qc.invalidateQueries({ queryKey: ['admin-modifiers'] });
    void qc.invalidateQueries({ queryKey: ['readiness-recipes'] });
    void qc.invalidateQueries({ queryKey: ['readiness'] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const patchLocation = useMutation({
    mutationFn: (v: { id: string; body: Record<string, unknown> }) =>
      api.patch(`/admin/locations/${v.id}`, v.body),
    onSuccess: refresh,
  });
  const setSchedule = useMutation({
    mutationFn: (v: { id: string; days: unknown[] }) =>
      api.put(`/admin/locations/${v.id}/schedule`, { days: v.days }),
    onSuccess: refresh,
  });
  const toggle = useMutation({
    mutationFn: (v: { id: string; body: Record<string, unknown> }) =>
      api.post(`/admin/locations/${v.id}/ordering`, v.body),
    onSuccess: refresh,
    // A refusal usually means the blockers changed; show the current list.
    onError: refresh,
  });
  const setPrice = useMutation({
    mutationFn: (v: { id: string; price_cents: number }) =>
      api.post(`/admin/menu/items/${v.id}/price`, { price_cents: v.price_cents }),
    onSuccess: refresh,
  });
  const setModifier = useMutation({
    mutationFn: (v: { id: string; price_delta_cents: number }) =>
      api.patch(`/admin/modifiers/${v.id}`, { price_delta_cents: v.price_delta_cents }),
    onSuccess: refresh,
  });
  const patchItem = useMutation({
    mutationFn: (v: {
      id: string;
      name: string;
      body: { servings_per_item?: number; is_spicy?: boolean; status?: 'active' | 'retired' };
    }) => api.patch(`/admin/menu/items/${v.id}`, v.body),
    onSuccess: refresh,
  });
  const showNutrition = useMutation({
    mutationFn: (v: { id: string; show: boolean }) =>
      api.patch(`/admin/locations/${v.id}`, { show_nutrition: v.show }),
    onSuccess: refresh,
  });
  const saveNotice = useMutation({
    mutationFn: (v: { id: string; text: string }) =>
      api.patch(`/admin/locations/${v.id}`, { allergen_notice: v.text }),
    onSuccess: refresh,
  });
  const showPrices = useMutation({
    mutationFn: (v: { id: string; show: boolean }) =>
      api.patch(`/admin/locations/${v.id}`, { show_prices: v.show }),
    onSuccess: refresh,
  });
  const [priceTarget, setPriceTarget] = useState<MenuItemRow | null>(null);
  const clearStatus = (m: { isSuccess: boolean; isError: boolean; reset: () => void }) => () => {
    if (m.isSuccess || m.isError) m.reset();
  };
  const [itemTarget, setItemTarget] = useState<MenuItemRow | null>(null);
  const [modTarget, setModTarget] = useState<ModifierRow | null>(null);
  const [menuNotice, setMenuNotice] = useState<string | null>(null);

  if (locs.isError) {
    const e = locs.error;
    const msg =
      e instanceof ApiError
        ? e.status === 401
          ? 'Your sign-in has expired. Sign out, then sign back in.'
          : e.status === 403
            ? `You are signed in, but this account is not set up as the owner. ${e.message}`
            : `This page did not load. ${e.message}`
        : 'This page did not load. Check the internet connection and try again.';
    return (
      <Card>
        <p style={{ color: 'var(--crave-red)', margin: 0 }}>{msg}</p>
      </Card>
    );
  }
  // The location chosen in the menu's location picker.
  const loc = locs.data?.find((l) => l.id === staff.location.id) ?? locs.data?.[0];
  if (!loc) return <p>Loading…</p>;

  const recipeFor = (id: string) => recipes.data?.find((r) => r.menu_item_id === id);
  const retire = async (it: MenuItemRow, reason: string | undefined) => {
    await api.patch(`/admin/menu/items/${it.id}`, {
      status: 'retired',
      ...(reason ? { reason } : {}),
    });
    setMenuNotice(`${it.name} is off the menu. It stays in past orders.`);
    refresh();
  };
  const itemBlocked = itemTarget?.can_delete === false;
  const itemConsequences = (it: MenuItemRow): string[] => {
    const out: string[] = [];
    const prices = it.prices?.length ?? 0;
    if (prices > 0) out.push(`Its price history (${plural(prices, 'price')})`);
    const recipe = recipeFor(it.id);
    if (recipe) out.push(`Its recipe, with ${plural(recipe.versions.length, 'version')}`);
    else if (!recipes.data) out.push('Its recipe, if it has one');
    const links = it.modifiers?.length ?? 0;
    if (links > 0)
      out.push(
        `Its links to ${plural(links, 'add-on or removal', 'add-ons and removals')}. They stay on other pizzas.`,
      );
    return out;
  };

  const modifierRows = modifiers.data;

  return (
    <section className="rd-page">
      <style>{READINESS_CSS}</style>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <h2 style={{ margin: 0 }}>{loc.name}</h2>
        <div className="rd-actions">
          {!canToggle ? (
            <span style={{ fontSize: 14, color: 'var(--ink-soft)' }}>
              Only the owner can turn ordering on, off or pause it.
            </span>
          ) : loc.orderingEnabled ? (
            <>
              <Button
                variant={loc.orderingPaused ? 'primary' : 'secondary'}
                disabled={toggle.isPending}
                onClick={() =>
                  toggle.mutate({
                    id: loc.id,
                    body: {
                      paused: !loc.orderingPaused,
                      paused_message: loc.orderingPaused
                        ? null
                        : 'We are catching up in the kitchen. Back in a few minutes.',
                    },
                  })
                }
              >
                {loc.orderingPaused ? 'Resume ordering' : 'Pause ordering'}
              </Button>
              <Button
                variant="danger"
                disabled={toggle.isPending}
                onClick={() => toggle.mutate({ id: loc.id, body: { enabled: false } })}
              >
                Turn ordering off
              </Button>
            </>
          ) : (
            <Button
              disabled={loc.blockers.length > 0 || toggle.isPending}
              onClick={() => toggle.mutate({ id: loc.id, body: { enabled: true } })}
            >
              Turn ordering on
            </Button>
          )}
        </div>
      </div>
      {toggle.isError && <Notice kind="error">{errorText(toggle.error)}</Notice>}
      <Card>
        <strong>Status:</strong> ordering is{' '}
        <strong>{loc.orderingEnabled ? (loc.orderingPaused ? 'paused' : 'on') : 'off'}</strong>.
        {loc.blockers.length > 0 ? (
          <ul style={{ marginBottom: 0 }}>
            {loc.blockers.map((b, i) => (
              <li key={i}>
                {b.code !== 'PAYMENTS_NOT_CONNECTED' ? (
                  b.message
                ) : (
                  <IntegrationNote
                    text={
                      /^Connect Stripe under/i.test(b.message)
                        ? 'Stripe is not connected.'
                        : b.message
                            .replace(
                              /\s*(Reconnect Stripe |Add the Stripe publishable key )?under Settings, Integrations\.?$/i,
                              '',
                            )
                            .trim() || b.message
                    }
                    action={
                      /^Connect Stripe under/i.test(b.message)
                        ? 'Connect Stripe'
                        : /publishable/i.test(b.message)
                          ? 'Add the publishable key'
                          : 'Reconnect Stripe'
                    }
                    canManage={canIntegrations}
                  />
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p style={{ margin: '8px 0 0', color: 'var(--ok)' }}>
            Everything needed to take orders is configured.
          </p>
        )}
        {loc.warnings?.map((w) => (
          <p key={w.code} style={{ margin: '8px 0 0', color: 'var(--warn)' }}>
            {w.code === 'EMAIL_NOT_CONNECTED' ? (
              <IntegrationNote
                text="Customers get no order emails."
                action="Connect email"
                canManage={canIntegrations}
              />
            ) : (
              w.message
            )}
          </p>
        ))}
      </Card>

      <Card>
        <h3 style={{ marginTop: 0 }}>Tax & timing</h3>
        <LocationForm
          loc={loc}
          onSave={(body) => patchLocation.mutate({ id: loc.id, body })}
          busy={patchLocation.isPending}
          status={patchLocation}
          onEdit={clearStatus(patchLocation)}
        />
      </Card>

      <Card>
        <h3 style={{ marginTop: 0 }}>Service days</h3>
        <ScheduleForm
          loc={loc}
          onSave={(days) => setSchedule.mutate({ id: loc.id, days })}
          busy={setSchedule.isPending}
          status={setSchedule}
          onEdit={clearStatus(setSchedule)}
        />
      </Card>

      <Card>
        <h3 style={{ marginTop: 0 }}>Menu and prices</h3>
        <p style={{ marginTop: 0, color: 'var(--ink-soft)' }}>
          Every pizza on the menu needs a confirmed price before ordering can open. Food cost
          appears once purchases are recorded.
          {canPrice ? '' : ' Only the owner can confirm pizza prices.'} Slices per pizza is how
          Nutrition Facts are split per slice. Retiring a pizza takes it off the menu and keeps it
          in past orders.
        </p>
        <PricesToggle
          shown={showPrices.isPending ? showPrices.variables.show : loc.showPrices !== false}
          canEdit={canPrice}
          busy={showPrices.isPending}
          status={showPrices}
          lastShow={showPrices.variables?.show}
          onChange={(show) => showPrices.mutate({ id: loc.id, show })}
        />
        {menuNotice && (
          <div style={{ marginBottom: 8 }}>
            <Notice kind="ok">{menuNotice}</Notice>
          </div>
        )}
        <div className="rd-prices">
          {items.data?.map((it) => {
            const active = it.prices?.find((p) => p.status === 'active' && p.location_id === null);
            const removable = it.prices?.some(
              (p) => (p.status === 'active' || p.status === 'draft') && p.location_id === null,
            );
            const cost = it.costing?.[0];
            return (
              <PriceRow
                // A new or removed price starts the price box fresh from what is saved.
                key={`${it.id}:${active?.id ?? 'none'}`}
                name={it.name}
                badge={
                  it.status === 'retired'
                    ? 'Off the menu'
                    : it.status === 'active'
                      ? undefined
                      : 'Not on the menu yet'
                }
                current={active?.price_cents ?? null}
                hint={
                  cost?.has_complete_cost && cost.food_cost_cents !== null
                    ? `Food cost ${formatCents(Math.round(cost.food_cost_cents))}${cost.food_cost_pct !== null ? ` · ${(cost.food_cost_pct * 100).toFixed(0)}%` : ''}`
                    : 'Food cost: unknown until purchases are recorded'
                }
                canEdit={canPrice}
                onSave={(cents) => setPrice.mutate({ id: it.id, price_cents: cents })}
                busy={setPrice.isPending}
                status={setPrice.variables?.id === it.id ? setPrice : null}
                onEdit={clearStatus(setPrice)}
                tools={
                  <MenuItemTools
                    item={it}
                    hasPrice={active !== undefined}
                    canMenu={canMenu}
                    canDelete={canDelete}
                    busy={patchItem.isPending}
                    status={patchItem.variables?.id === it.id ? patchItem : null}
                    lastChange={patchItem.variables?.body}
                    onPatch={(body) => {
                      setMenuNotice(null);
                      patchItem.mutate({ id: it.id, name: it.name, body });
                    }}
                    onEdit={clearStatus(patchItem)}
                    onDelete={() => {
                      setMenuNotice(null);
                      setItemTarget(it);
                    }}
                    onRemovePrice={
                      canPrice && removable
                        ? () => {
                            setMenuNotice(null);
                            setPriceTarget(it);
                          }
                        : undefined
                    }
                  />
                }
              />
            );
          })}
        </div>
        {modifierRows ? (
          <>
            <h4 style={{ margin: '20px 0 4px' }}>Add-ons and removals</h4>
            <p className="rd-muted" style={{ margin: 0 }}>
              Add-ons cost extra. Removals let customers leave something off for free.
            </p>
            <div className="rd-prices">
              {modifierRows.map((m) => {
                const addOn = m.kind === 'ADD_INGREDIENT';
                const where =
                  m.menu_items === 0 ? 'Not on any pizza' : `On ${plural(m.menu_items, 'pizza')}`;
                return (
                  <PriceRow
                    key={m.id}
                    name={m.name}
                    badge={m.is_active ? undefined : 'Hidden'}
                    current={m.price_delta_cents}
                    priceless={!addOn}
                    hint={
                      addOn ? `Added to the pizza price · ${where}` : `Free to leave off · ${where}`
                    }
                    canEdit={canAddOns}
                    onSave={(cents) => setModifier.mutate({ id: m.id, price_delta_cents: cents })}
                    busy={setModifier.isPending}
                    status={setModifier.variables?.id === m.id ? setModifier : null}
                    onEdit={clearStatus(setModifier)}
                    tools={
                      <div className="rd-item-actions">
                        {canDelete && (
                          <Button
                            type="button"
                            variant="danger"
                            onClick={() => {
                              setMenuNotice(null);
                              setModTarget(m);
                            }}
                          >
                            Delete
                          </Button>
                        )}
                        <HistoryLink entityType="modifiers" entityId={m.id} name={m.name} />
                      </div>
                    }
                  />
                );
              })}
            </div>
          </>
        ) : (
          <>
            <h4 style={{ margin: '16px 0 0' }}>Add-ons</h4>
            <div className="rd-prices">
              {[
                ...new Map(
                  (items.data ?? [])
                    .flatMap((it) => it.modifiers ?? [])
                    .filter((m) => m.kind === 'ADD_INGREDIENT')
                    .map((m) => [m.id, m]),
                ).values(),
              ].map((m) => (
                <PriceRow
                  key={m.id}
                  name={m.name}
                  current={m.price_delta_cents}
                  hint="Added to the pizza price"
                  canEdit={canAddOns}
                  onSave={(cents) => setModifier.mutate({ id: m.id, price_delta_cents: cents })}
                  busy={setModifier.isPending}
                  status={setModifier.variables?.id === m.id ? setModifier : null}
                  onEdit={clearStatus(setModifier)}
                />
              ))}
            </div>
          </>
        )}
      </Card>

      <Card>
        <h3 style={{ marginTop: 0 }}>Nutrition and allergens on the ordering site</h3>
        <p style={{ marginTop: 0, color: 'var(--ink-soft)' }}>
          Nutrition Facts come from each pizza&apos;s published recipe, using raw ingredient weights
          before baking. They are estimates and do not include add-ons.
        </p>
        <NutritionToggle
          shown={showNutrition.isPending ? showNutrition.variables.show : !!loc.showNutrition}
          busy={showNutrition.isPending}
          status={showNutrition}
          lastShow={showNutrition.variables?.show}
          onChange={(show) => showNutrition.mutate({ id: loc.id, show })}
        />
        {recipes.data && (
          <NutritionGaps
            shown={!!loc.showNutrition}
            gaps={(items.data ?? [])
              .filter((it) => it.status === 'active')
              .flatMap((it) => {
                const why = nutritionGap(it, recipes.data);
                return why ? [{ id: it.id, name: it.name, why }] : [];
              })}
          />
        )}
        {recipes.isError && (
          <p className="rd-muted" style={{ margin: '8px 0 0' }}>
            Could not check which pizzas have complete nutrition. {errorText(recipes.error)}
          </p>
        )}
        <div style={{ borderTop: '1px solid var(--line)', margin: '20px 0 16px' }} />
        <AllergenNoticeForm
          saved={loc.allergenNotice ?? STANDARD_ALLERGEN_NOTICE}
          onSave={(text) => saveNotice.mutate({ id: loc.id, text })}
          busy={saveNotice.isPending}
          status={saveNotice}
          onEdit={clearStatus(saveNotice)}
        />
      </Card>

      <ConfirmDelete
        open={itemTarget !== null}
        onClose={() => setItemTarget(null)}
        title={
          itemTarget
            ? itemBlocked
              ? `${itemTarget.name} can't be deleted`
              : `Delete ${itemTarget.name}?`
            : ''
        }
        description={
          itemTarget
            ? itemBlocked
              ? (itemTarget.delete_blocked_reason ?? 'It is on past orders.')
              : 'It comes off the menu and the ordering site right away.'
            : undefined
        }
        consequences={itemTarget && !itemBlocked ? itemConsequences(itemTarget) : undefined}
        confirmText={itemTarget && !itemBlocked ? itemTarget.name : undefined}
        askReason
        actionLabel={itemBlocked ? 'Retire from menu' : 'Delete'}
        pendingLabel={itemBlocked ? 'Retiring…' : 'Deleting…'}
        onConfirm={async ({ reason }) => {
          if (!itemTarget) return;
          if (itemBlocked) {
            await retire(itemTarget, reason);
            return;
          }
          await api.delete(withReason(`/admin/menu/items/${itemTarget.id}`, reason));
          setMenuNotice(`Deleted ${itemTarget.name}.`);
          refresh();
        }}
        alternative={
          itemTarget && !itemBlocked && itemTarget.status !== 'retired'
            ? {
                label: 'Retire from menu',
                description: 'Retiring takes it off the menu and keeps it in past orders.',
                pendingLabel: 'Retiring…',
                onClick: ({ reason }) => retire(itemTarget, reason),
              }
            : undefined
        }
      />
      <ConfirmDelete
        open={modTarget !== null}
        onClose={() => setModTarget(null)}
        title={modTarget ? `Delete ${modTarget.name}?` : ''}
        description={
          modTarget
            ? `It comes off every pizza right away.${modTarget.order_lines > 0 ? ' Past orders keep its name.' : ''}`
            : undefined
        }
        consequencesTitle="This also removes:"
        consequences={
          modTarget && modTarget.menu_items > 0
            ? [
                `Its place on ${plural(modTarget.menu_items, 'pizza')}. The pizzas stay on the menu.`,
              ]
            : undefined
        }
        askReason
        onConfirm={async ({ reason }) => {
          if (!modTarget) return;
          await api.delete(withReason(`/admin/modifiers/${modTarget.id}`, reason));
          setMenuNotice(`Deleted ${modTarget.name}.`);
          refresh();
        }}
      />
      <ConfirmDelete
        open={priceTarget !== null}
        onClose={() => setPriceTarget(null)}
        permission="prices.confirm"
        title={priceTarget ? `Remove the price of ${priceTarget.name}?` : ''}
        description={
          priceTarget ? (
            <>
              <p style={{ margin: 0 }}>
                {!priceTarget.prices?.some((p) => p.status === 'active' && p.location_id === null)
                  ? `${priceTarget.name} goes back to having no price.`
                  : priceTarget.status === 'active' && loc.orderingEnabled
                    ? `Online ordering is on. Customers won't be able to order ${priceTarget.name} until you confirm a new price.`
                    : priceTarget.status === 'active'
                      ? `${priceTarget.name} will need a new price before ordering can open.`
                      : `${priceTarget.name} goes back to having no price.`}
              </p>
              <p style={{ margin: '8px 0 0' }}>
                Orders already placed keep the price they were charged.
              </p>
            </>
          ) : undefined
        }
        askReason
        actionLabel="Remove price"
        pendingLabel="Removing…"
        onConfirm={async ({ reason }) => {
          if (!priceTarget) return;
          await api.delete(withReason(`/admin/menu/items/${priceTarget.id}/price`, reason));
          setMenuNotice(
            `${priceTarget.name} has no price now. Confirm a new price when you are ready.`,
          );
          refresh();
        }}
      />
    </section>
  );
}

/** A setup problem fixed under Integrations: a link for the owner, a pointer for everyone else. */
function IntegrationNote({
  text,
  action,
  canManage,
}: {
  text: string;
  action: string;
  canManage: boolean;
}) {
  return (
    <>
      {text}{' '}
      {canManage ? (
        <>
          <Link
            to="/settings/integrations"
            style={{ color: 'var(--ink)', fontWeight: 600, textUnderlineOffset: 3 }}
          >
            {action} under Integrations
          </Link>
          .
        </>
      ) : (
        'The owner fixes this under Integrations.'
      )}
    </>
  );
}

function SaveStatus({ status, okText = 'Saved.' }: { status: SaveState; okText?: string }) {
  if (status.isError)
    return (
      <span role="alert" style={{ color: 'var(--crave-red)', fontSize: 14 }}>
        {errorText(status.error)}
      </span>
    );
  if (status.isSuccess)
    return (
      <span role="status" style={{ color: 'var(--ok)', fontSize: 14, fontWeight: 600 }}>
        {okText}
      </span>
    );
  return null;
}

interface LocationErrors {
  tax?: string;
  lead?: string;
  cap?: string;
  grace?: string;
  instr?: string;
}

function LocationForm({
  loc,
  onSave,
  busy,
  status,
  onEdit,
}: {
  loc: Location;
  onSave: (body: Record<string, unknown>) => void;
  busy: boolean;
  status: SaveState;
  onEdit: () => void;
}) {
  const [tax, setTax] = useState(loc.taxRateBps === null ? '' : (loc.taxRateBps / 100).toFixed(2));
  const [lead, setLead] = useState(String(loc.minLeadMinutes));
  const [cap, setCap] = useState(String(loc.defaultSlotCapacity));
  const [grace, setGrace] = useState(String(loc.noShowGraceMinutes));
  const [instr, setInstr] = useState(loc.pickupInstructions ?? '');
  const [errors, setErrors] = useState<LocationErrors>({});
  const edit = (set: (v: string) => void) => (v: string) => {
    set(v);
    onEdit();
  };
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const next: LocationErrors = {};
        let taxBps: number | null = null;
        if (tax.trim() !== '') {
          const pct = parsePercent(tax);
          if (pct === null) next.tax = 'Enter a percent like 7 or 7.25.';
          else if (pct > 20) next.tax = 'The tax rate must be 20% or less.';
          else taxBps = Math.round(pct * 100);
        }
        const leadN = parseWhole(lead);
        if (leadN === null || leadN > 10080) next.lead = 'Enter whole minutes, from 0 to 10080.';
        const capN = parseWhole(cap);
        if (capN === null || capN > 100) next.cap = 'Enter a whole number, from 0 to 100.';
        const graceN = parseWhole(grace);
        if (graceN === null || graceN > 240) next.grace = 'Enter whole minutes, from 0 to 240.';
        if (instr.length > 500) next.instr = 'Keep pickup instructions under 500 characters.';
        setErrors(next);
        if (Object.keys(next).length > 0) {
          onEdit();
          return;
        }
        onSave({
          tax_rate_bps: taxBps,
          min_lead_minutes: leadN,
          default_slot_capacity: capN,
          no_show_grace_minutes: graceN,
          pickup_instructions: instr.trim() || null,
        });
      }}
      style={{ display: 'grid', gap: 4 }}
    >
      <FormGrid min={200}>
        <Field
          label="Sales tax rate (%)"
          value={tax}
          onChange={(e) => edit(setTax)(e.target.value)}
          inputMode="decimal"
          placeholder="e.g. 7.00"
          hint="Florida state + county rate for prepared food. Blank means not set."
          error={errors.tax}
        />
        <Field
          label="Minimum lead time (minutes)"
          value={lead}
          onChange={(e) => edit(setLead)(e.target.value)}
          inputMode="numeric"
          error={errors.lead}
        />
        <Field
          label="Pizzas per 30-minute window"
          value={cap}
          onChange={(e) => edit(setCap)(e.target.value)}
          inputMode="numeric"
          hint="One oven bakes about 5 to 7 pizzas per half hour."
          error={errors.cap}
        />
        <Field
          label="No-show grace (minutes)"
          value={grace}
          onChange={(e) => edit(setGrace)(e.target.value)}
          inputMode="numeric"
          error={errors.grace}
        />
        <Field
          label="Pickup instructions"
          value={instr}
          onChange={(e) => edit(setInstr)(e.target.value)}
          wide
          placeholder="Where to find us and what to say."
          error={errors.instr}
        />
      </FormGrid>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
        <Button type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
        <SaveStatus status={status} okText="Tax and timing saved." />
      </div>
    </form>
  );
}

function ScheduleForm({
  loc,
  onSave,
  busy,
  status,
  onEdit,
}: {
  loc: Location;
  onSave: (days: unknown[]) => void;
  busy: boolean;
  status: SaveState;
  onEdit: () => void;
}) {
  const [days, setDays] = useState(
    DAYS.map((_, weekday) => {
      const s = loc.schedule.find((x) => x.weekday === weekday);
      return {
        weekday,
        opens_at: s ? hhmm(s.opensAt) : '11:00',
        closes_at: s ? hhmm(s.closesAt) : '18:00',
        is_enabled: s?.isEnabled ?? false,
      };
    }),
  );
  const [problems, setProblems] = useState<string[]>([]);
  const set = (i: number, patch: Partial<(typeof days)[number]>) => {
    setDays((d) => d.map((x, j) => (j === i ? { ...x, ...patch } : x)));
    onEdit();
  };
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const found: string[] = [];
        const out = days.map((d) => {
          const hasTimes = HHMM.test(d.opens_at) && HHMM.test(d.closes_at);
          if (hasTimes && d.closes_at > d.opens_at) return d;
          if (d.is_enabled) {
            found.push(
              hasTimes
                ? `${DAYS[d.weekday]}: the closing time must be after the opening time.`
                : `${DAYS[d.weekday]}: set an opening and a closing time.`,
            );
            return d;
          }
          // A day that is off still needs valid times; keep the saved ones.
          const saved = loc.schedule.find((x) => x.weekday === d.weekday);
          return {
            ...d,
            opens_at: saved ? hhmm(saved.opensAt) : '11:00',
            closes_at: saved ? hhmm(saved.closesAt) : '18:00',
          };
        });
        setProblems(found);
        if (found.length > 0) {
          onEdit();
          return;
        }
        onSave(out);
      }}
      style={{ display: 'grid', gap: 16 }}
    >
      <div className="rd-days">
        <div className="rd-days-head" aria-hidden="true">
          <span>Day</span>
          <span>Opens</span>
          <span>Closes</span>
        </div>
        {days.map((d, i) => (
          <div key={d.weekday} className="rd-day">
            <label className="rd-check">
              <input
                type="checkbox"
                checked={d.is_enabled}
                onChange={(e) => set(i, { is_enabled: e.target.checked })}
              />
              {DAYS[d.weekday]}
            </label>
            <label className="rd-time">
              <span className="rd-time-label" aria-hidden="true">
                Opens
              </span>
              <input
                type="time"
                value={d.opens_at}
                onChange={(e) => set(i, { opens_at: e.target.value })}
                aria-label={`${DAYS[d.weekday]} opens`}
              />
            </label>
            <label className="rd-time">
              <span className="rd-time-label" aria-hidden="true">
                Closes
              </span>
              <input
                type="time"
                value={d.closes_at}
                onChange={(e) => set(i, { closes_at: e.target.value })}
                aria-label={`${DAYS[d.weekday]} closes`}
              />
            </label>
          </div>
        ))}
      </div>
      {problems.length > 0 && (
        <Notice kind="error">
          {problems.map((p) => (
            <div key={p}>{p}</div>
          ))}
        </Notice>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
        <Button type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Save service days'}
        </Button>
        <SaveStatus status={status} okText="Service days saved." />
      </div>
    </form>
  );
}

function PriceRow({
  name,
  badge,
  current,
  hint,
  canEdit,
  priceless = false,
  onSave,
  busy,
  status,
  onEdit,
  tools,
}: {
  name: string;
  /** A short label beside the name, like "Off the menu". */
  badge?: string | undefined;
  current: number | null;
  hint: string;
  canEdit: boolean;
  /** No price to set (a free removal): the row shows its name, hint and tools only. */
  priceless?: boolean;
  onSave: (cents: number) => void;
  busy: boolean;
  status: SaveState | null;
  onEdit: () => void;
  /** A line under the row for slices, retire, delete and history. Buttons must be type="button". */
  tools?: ReactNode;
}) {
  const [v, setV] = useState(current === null ? '' : (current / 100).toFixed(2));
  const cents = parseDollarsToCents(v);
  const label = (
    <>
      <strong className="rd-price-name">
        {name}
        {badge && <span className="rd-badge">{badge}</span>}
      </strong>
      <div className="rd-price-meta">
        {priceless
          ? hint
          : `${current === null ? 'No price yet' : `Current ${formatCents(current)}`} · ${hint}`}
      </div>
    </>
  );
  const toolsLine = tools ? <div className="rd-tools">{tools}</div> : null;
  if (!canEdit || priceless)
    return (
      <div className="rd-price rd-price-ro">
        {label}
        {!priceless && <span className="rd-price-owner">Owner only</span>}
        {toolsLine}
      </div>
    );
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (cents !== null && cents > 0) onSave(cents);
      }}
      className="rd-price"
    >
      {label}
      <Field
        label="Price ($)"
        value={v}
        onChange={(e) => {
          setV(e.target.value);
          onEdit();
        }}
        inputMode="decimal"
        placeholder="0.00"
        error={v.trim() !== '' && cents === null ? 'Dollars, like 12.50' : undefined}
      />
      <div className="rd-price-btn">
        <Button
          type="submit"
          variant={current === null ? 'primary' : 'secondary'}
          disabled={busy || cents === null || cents <= 0 || cents === current}
        >
          {current === null ? 'Confirm' : 'Update'}
        </Button>
      </div>
      {status && (status.isSuccess || status.isError) && (
        <div className="rd-price-status">
          <SaveStatus status={status} okText={`${name} price saved.`} />
        </div>
      )}
      {toolsLine}
    </form>
  );
}

/** Slices per pizza, retire or put back, delete and history for one menu item. */
function MenuItemTools({
  item,
  hasPrice,
  canMenu,
  canDelete,
  busy,
  status,
  lastChange,
  onPatch,
  onEdit,
  onDelete,
  onRemovePrice,
}: {
  item: MenuItemRow;
  hasPrice: boolean;
  canMenu: boolean;
  canDelete: boolean;
  busy: boolean;
  status: SaveState | null;
  lastChange:
    { servings_per_item?: number; is_spicy?: boolean; status?: 'active' | 'retired' } | undefined;
  onPatch: (body: {
    servings_per_item?: number;
    is_spicy?: boolean;
    status?: 'active' | 'retired';
  }) => void;
  onEdit: () => void;
  onDelete: () => void;
  /** Opens the remove price dialog; undefined when there is no price or no permission. */
  onRemovePrice?: (() => void) | undefined;
}) {
  const id = useId();
  const saved = item.servings_per_item ?? 6;
  const [slices, setSlices] = useState(String(saved));
  const [error, setError] = useState<string | null>(null);
  const n = parseWhole(slices);
  const save = () => {
    if (n === null || n < 1 || n > 64) {
      setError('Slices per pizza must be a whole number from 1 to 64.');
      return;
    }
    if (n !== saved) onPatch({ servings_per_item: n });
  };
  const onMenu = item.status === 'active';
  const blocked = item.can_delete === false;
  // Retiring answers a block from past orders or tastings, not the birthday pizza setting.
  const retireHelps = Object.entries(item.uses ?? {}).some(
    ([k, used]) => !BIRTHDAY_USES.has(k) && Number(used) > 0,
  );
  const showDelete =
    canDelete && canMenu && (!blocked || (item.status !== 'retired' && retireHelps));
  const okText =
    lastChange?.status === 'retired'
      ? `${item.name} is off the menu. It stays in past orders.`
      : lastChange?.status === 'active'
        ? `${item.name} is on the menu.`
        : lastChange?.is_spicy === true
          ? `${item.name} shows SPICY wherever it is offered.`
          : lastChange?.is_spicy === false
            ? `${item.name} no longer shows SPICY.`
            : 'Slices per pizza saved.';

  return (
    <>
      {canMenu ? (
        <div className="rd-slices">
          <label htmlFor={id}>Slices per pizza</label>
          <input
            id={id}
            className="rd-small-input"
            inputMode="numeric"
            value={slices}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${id}-error` : undefined}
            onChange={(e) => {
              setSlices(e.target.value);
              setError(null);
              onEdit();
            }}
            onKeyDown={(e) => {
              // Enter saves the slices, not the price form around this row.
              if (e.key === 'Enter') {
                e.preventDefault();
                save();
              }
            }}
          />
          <Button type="button" variant="secondary" onClick={save} disabled={busy || n === saved}>
            Save
          </Button>
        </div>
      ) : (
        <span className="rd-muted">{plural(saved, 'slice')} per pizza</span>
      )}
      {canMenu ? (
        <label className="rd-spicy">
          <input
            type="checkbox"
            checked={item.is_spicy === true}
            disabled={busy}
            onChange={(e) => onPatch({ is_spicy: e.target.checked })}
          />
          <span>
            Spicy <span className="rd-muted">— shows SPICY beside the name</span>
          </span>
        </label>
      ) : (
        item.is_spicy && <Spicy size="sm" />
      )}
      <div className="rd-item-actions">
        {canMenu &&
          (onMenu ? (
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => onPatch({ status: 'retired' })}
            >
              Retire from menu
            </Button>
          ) : item.status === 'retired' || hasPrice ? (
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => onPatch({ status: 'active' })}
            >
              {item.status === 'retired' ? 'Put back on menu' : 'Put on the menu'}
            </Button>
          ) : null)}
        {onRemovePrice && (
          <Button type="button" variant="secondary" disabled={busy} onClick={onRemovePrice}>
            Remove price
          </Button>
        )}
        {showDelete && (
          <Button type="button" variant="danger" onClick={onDelete}>
            Delete
          </Button>
        )}
        <HistoryLink entityType="menu_items" entityId={item.id} name={item.name} />
      </div>
      {canDelete && blocked && !showDelete && (
        <span className="rd-tools-msg rd-muted">
          {item.status === 'retired' && retireHelps
            ? "It stays in past orders, so it can't be deleted."
            : item.delete_blocked_reason}
        </span>
      )}
      {error && (
        <span
          id={`${id}-error`}
          role="alert"
          className="rd-tools-msg"
          style={{ color: 'var(--crave-red)' }}
        >
          {error}
        </span>
      )}
      {status && (status.isSuccess || status.isError) && (
        <span className="rd-tools-msg">
          <SaveStatus status={status} okText={okText} />
        </span>
      )}
    </>
  );
}

function NutritionToggle({
  shown,
  busy,
  status,
  lastShow,
  onChange,
}: {
  shown: boolean;
  busy: boolean;
  status: SaveState;
  lastShow: boolean | undefined;
  onChange: (show: boolean) => void;
}) {
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <label className="rd-check" style={{ fontWeight: 600 }}>
        <input
          type="checkbox"
          checked={shown}
          disabled={busy}
          onChange={(e) => onChange(e.target.checked)}
        />
        Show Nutrition Facts on the ordering site
      </label>
      <SaveStatus
        status={status}
        okText={
          lastShow
            ? 'Nutrition Facts now show on the ordering site.'
            : 'Nutrition Facts are hidden on the ordering site.'
        }
      />
    </div>
  );
}

/** "Show prices to customers": a switch for the owner, a plain status line for everyone else. */
function PricesToggle({
  shown,
  canEdit,
  busy,
  status,
  lastShow,
  onChange,
}: {
  shown: boolean;
  canEdit: boolean;
  busy: boolean;
  status: SaveState;
  lastShow: boolean | undefined;
  onChange: (show: boolean) => void;
}) {
  return (
    <div style={{ display: 'grid', gap: 4, marginBottom: 12 }}>
      <label className="rd-check" style={{ fontWeight: 600 }}>
        <input
          type="checkbox"
          role="switch"
          checked={shown}
          disabled={busy || !canEdit}
          onChange={(e) => onChange(e.target.checked)}
        />
        Show prices to customers
      </label>
      <span className="rd-muted">
        {shown
          ? 'Prices show on the menu and pizza pages.'
          : 'The menu and pizza pages show no prices. Customers still see their total in the cart and at checkout before they pay.'}
        {canEdit ? '' : ' Only the owner can change this.'}
      </span>
      <SaveStatus
        status={status}
        okText={
          lastShow
            ? 'Prices now show on the ordering site.'
            : 'Prices are hidden on the menu and pizza pages.'
        }
      />
    </div>
  );
}

function NutritionGaps({
  shown,
  gaps,
}: {
  shown: boolean;
  gaps: { id: string; name: string; why: string }[];
}) {
  if (gaps.length === 0)
    return (
      <p style={{ margin: '8px 0 0', color: 'var(--ok)', fontSize: 14 }}>
        Every pizza on the menu has complete nutrition.
      </p>
    );
  return (
    <div className="rd-gaps" style={{ marginTop: 8 }} role="status">
      <strong style={{ color: 'var(--warn)' }}>
        {shown
          ? 'These pizzas show no Nutrition Facts until their nutrition is complete:'
          : 'If you turn this on, these pizzas will show no Nutrition Facts until their nutrition is complete:'}
      </strong>
      <ul>
        {gaps.map((g) => (
          <li key={g.id}>
            <strong>{g.name}</strong> {g.why}.
          </li>
        ))}
      </ul>
      Add nutrition under <Link to="/ingredients">Ingredients</Link>, then publish the recipe under{' '}
      <Link to="/recipes">Recipes</Link>.
    </div>
  );
}

function AllergenNoticeForm({
  saved,
  onSave,
  busy,
  status,
  onEdit,
}: {
  saved: string;
  onSave: (text: string) => void;
  busy: boolean;
  status: SaveState;
  onEdit: () => void;
}) {
  const [text, setText] = useState(saved);
  const [error, setError] = useState<string | undefined>(undefined);
  const standard = text.trim() === STANDARD_ALLERGEN_NOTICE;
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const t = text.trim();
        if (t === '') {
          setError('Write a notice, or use the standard one.');
          onEdit();
          return;
        }
        onSave(t);
      }}
      style={{ display: 'grid', gap: 12 }}
    >
      <TextAreaField
        label="Allergen notice"
        hint="Shown on the ordering site's menu so customers with allergies know what your kitchen handles."
        value={text}
        maxLength={500}
        rows={3}
        error={error}
        onChange={(e) => {
          setText(e.target.value);
          setError(undefined);
          onEdit();
        }}
      />
      {!standard && (
        <p className="rd-muted" style={{ margin: 0 }}>
          Standard notice: “{STANDARD_ALLERGEN_NOTICE}”
        </p>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
        <Button type="submit" disabled={busy || text.trim() === saved.trim()}>
          {busy ? 'Saving…' : 'Save notice'}
        </Button>
        {!standard && (
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setText(STANDARD_ALLERGEN_NOTICE);
              setError(undefined);
              onEdit();
            }}
          >
            Use the standard notice
          </Button>
        )}
        <SaveStatus status={status} okText="Allergen notice saved." />
      </div>
    </form>
  );
}
